import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FaChevronDown, FaPen, FaTrash } from 'react-icons/fa';
import DeleteEpisodeDialog from './DeleteEpisodeDialog';
// ADR-176 — a Pending amendment is revised through its own verb, not the event editor
import EditAmendmentDialog from '../../../components/EditAmendmentDialog';
import { canReviseAmendmentEvent } from '../../../../../constants/amendmentRevision';
import { useAlert } from '../../../../../contexts/AlertContext';
import { employmentApi } from '../../../../../services/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import EmptyState from '../../../../common/EmptyState';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import Modal from '../../../../common/Modal';
import { FormContainer, ResultCount } from './styles';
import { FormSection } from '../../styles';
import { COLORS } from '../../../../../constants/radar';
import { useAbility } from '../../../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../../../constants/permissions';
import {
  DEFAULT_SOURCE_SYSTEM,
  isRadarSourced,
  toSourceContractId,
} from '../../../../../constants/employmentSourceSystems';
// ADR-138 — shared with the hire-date episode picker so the two surfaces read an
// episode's source reference identically.
import { episodeSourceRef } from '../../../../../constants/employmentEpisodes';
import { END_REASON_OPTIONS } from '../../../../../constants/employmentEndReasons';
import {
  isActiveEpisode,
  canEditEpisode,
  canEditEventsOf,
  validateClosedEpisodeForm,
  needsConfirmation,
  warningLines,
} from '../../../../../utils/closedEpisodeCorrection';
import { contractIdentityLabel } from '../../../../../constants/contractActions';
import { EVENT_ACTOR_TYPES, getActorTypeColor } from '../../../../../constants/employmentActorTypes';
import { EVENT_VALIDATION_STATES, getValidationStateColor, getValidationStateTextColor } from '../../../../../constants/employmentValidationStates';
// ADR-136 phase 6 — the payload-derived captions moved to a shared module so
// the "already scheduled" panel in the end-date modals reads the same text.
import { employmentEventAnnotation, amendmentCaptionParts } from '../../../../../constants/employmentEventAnnotations';

dayjs.extend(utc);

// Terminal event states — only these events may be edited (ADR-019).
// A `Pending` event is still owned by the scheduler and is not editable.
const TERMINAL_EVENT_STATES = [
  EVENT_VALIDATION_STATES.VALID,
  EVENT_VALIDATION_STATES.CANCELLED,
  EVENT_VALIDATION_STATES.INVALID,
  EVENT_VALIDATION_STATES.SKIPPED,
];

const isTerminalEvent = (event) => TERMINAL_EVENT_STATES.includes(event.eventValidationState);

// ADR-136 phase 6 — §15 slice-3 follow-up (settled 2026-08-06): the
// scheduled-renewal rows explain themselves. The caption derives at RENDER TIME
// from the event's stored payload — the single source of truth the sweep
// replays — so it can never drift from what enactment will actually do. The
// alternative (marker event types for "old contract ends here") was rejected:
// duplicated bookkeeping that every cancel/enact transition must keep in
// lockstep, for no information the payload doesn't already carry.
// (the caption entry point — `employmentEventAnnotation`, covering renewal AND
// amendment captions since ADR-143 — lives in constants/employmentEventAnnotations.js,
// shared with the end-date modals' "already scheduled" panel.)

// ADR-019 made only the active (open) episode editable. ADR-168 opens CLOSED
// episodes and their terminal events to holders of the history-correction
// right too; the rules live in utils/closedEpisodeCorrection.js
// (isActiveEpisode, canEditEpisode, canEditEventsOf).

// Stored @db.Date (ISO) -> a UTC dayjs so the picker's calendar date matches the
// value displayed in the table (which also uses dayjs.utc). null when empty.
const toDayjs = (val) => (val ? dayjs.utc(val) : null);

// dayjs -> "YYYY-MM-DD" for the API (date-only). null when empty.
const toApiDate = (d) => (d && dayjs.isDayjs(d) && d.isValid() ? d.format('YYYY-MM-DD') : null);

// An episode is already linked to its source contract once its payload carries a
// sourceReference.contractId. The API refuses to re-link it (ADR-049 anti-steal),
// so the UI shows the link read-only in that case.
// (`episodeSourceRef` moved to constants/employmentEpisodes.js for ADR-138.)
const isEpisodeLinked = (episode) => episodeSourceRef(episode).contractId != null;

// ADR-136 A5: the annotation right only applies to episodes this system owns —
// one stamped 'radar', or one with no stamp at all (created here, or predating
// the stamp). An ingestion-owned episode is excluded because that sync maintains
// its comments (ADR-031); the API enforces the same rule with a 409.
const isAnnotatableEpisode = (episode) => isRadarSourced(episodeSourceRef(episode));

// ADR-136: the source contract link is editable while the episode has no link yet
// (the ADR-049 backfill case) AND — new — when the link is one WE own, so a number
// typed at hire/rehire can be corrected. A link owned by an ingestion source stays
// read-only: there the contract id is authoritative source data and the API refuses
// to re-point it (409).
const isSourceRefEditable = (episode) =>
  !isEpisodeLinked(episode) || isRadarSourced(episodeSourceRef(episode));

// ADR-136 §15 / ADR-143 §12 — the payload-driven caption under an event's
// type. ADR-151: an amendment that changed two or more fields renders those
// changes as a LIST. Everything else — a single-change amendment, the
// renewal rows — keeps the one-line sentence, byte for byte, and an
// uncaptioned code still renders nothing (ADR-143 §12).
const CAPTION_SX = { color: '#475569', display: 'block', fontStyle: 'italic', fontWeight: 500 };

const EventCaption = ({ event }) => {
  const parts = amendmentCaptionParts(event);

  if (parts) {
    return (
      <Box sx={{ mt: 0.25 }}>
        <Typography variant="caption" sx={CAPTION_SX}>{parts.lead}</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.25, listStylePosition: 'outside' }}>
          {parts.items.map((item, index) => (
            <Typography component="li" variant="caption" sx={CAPTION_SX} key={`${index}-${item}`}>
              {item}
            </Typography>
          ))}
        </Box>
        {parts.trail && <Typography variant="caption" sx={CAPTION_SX}>{parts.trail}</Typography>}
      </Box>
    );
  }

  const sentence = employmentEventAnnotation(event);
  if (!sentence) return null;
  return <Typography variant="caption" sx={{ ...CAPTION_SX, mt: 0.25 }}>{sentence}</Typography>;
};

const EmploymentEpisodes = ({ employeeId, primaryEmploymentEpisodeId = null }) => {
  const { showAlert } = useAlert();
  const ability = useAbility();
  const [episodes, setEpisodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const openEpisodeCount = episodes.filter((e) => !e.endDate).length;

  // ADR-019 grants the full correction capability (dates + comments + source ref,
  // and separately the event edits below). ADR-136 A5 adds a strictly narrower
  // right: comments + source contract link, on episodes this system owns. A
  // holder of the full right keeps today's modal unchanged.
  const canModifyEpisodes = ability.can(ACTIONS.UPDATE, SUBJECTS.EMPLOYMENT_EPISODE_MODIFICATION);
  const canAnnotateEpisodes = ability.can(ACTIONS.UPDATE, SUBJECTS.EMPLOYMENT_EPISODE_ANNOTATION);
  // Which surface a given episode's edit button opens. Full-right holders always
  // get the modification modal; annotation-only holders get the narrow one, and
  // only on open episodes they are allowed to annotate (showEpisodeEditor below).
  // The modal is annotation-only when the caller lacks the full right.
  const isAnnotationOnly = !canModifyEpisodes;
  // ADR-165 A5 — delete a contract to correct data (admin, or anyone granted
  // `delete EmploymentEpisode`). Any card, open or closed.
  const canDeleteEpisodes = ability.can(ACTIONS.DELETE, SUBJECTS.EMPLOYMENT_EPISODE);
  // ADR-184 — the bin is hidden where the server always refuses (ADR-165
  // blockers): the primary contract and the person's only contract. The
  // dialog still shows any blocker the server finds, as a backstop.
  const canDeleteEpisode = (episode) => canDeleteEpisodes
    && episode.id !== primaryEmploymentEpisodeId
    && episodes.length > 1;
  // ADR-168 D8 — correcting a CLOSED contract and its events needs this right
  // on top of the full ADR-019 one.
  const canCorrectHistory = ability.can(ACTIONS.UPDATE, SUBJECTS.EMPLOYMENT_EPISODE_HISTORY_CORRECTION);
  const editRights = {
    canModify: canModifyEpisodes,
    canAnnotate: canAnnotateEpisodes,
    canCorrectHistory,
  };
  const showEpisodeEditor = (episode) =>
    canEditEpisode(episode, { ...editRights, isAnnotatable: isAnnotatableEpisode(episode) });
  const showEventEditors = (episode) => canEditEventsOf(episode, editRights);
  // ADR-176 — admin-only: revise a Pending amendment (both rights), on an open
  // contract this system owns. The server re-checks everything.
  const canReviseAmendments = ability.can(ACTIONS.UPDATE, SUBJECTS.CONTRACT_AMENDMENT)
    && ability.can(ACTIONS.UPDATE, SUBJECTS.CONTRACT_AMENDMENT_REVISION);
  const canReviseEventOn = (event, episode) => canReviseAmendments
    && isActiveEpisode(episode) && isAnnotatableEpisode(episode) && canReviseAmendmentEvent(event);
  const hasRevisableEvent = (episode) =>
    (episode.employmentEvents || []).some((event) => canReviseEventOn(event, episode));
  const showEventActions = (episode) => showEventEditors(episode) || hasRevisableEvent(episode);
  const [revisingAmendment, setRevisingAmendment] = useState(null); // { episodeId, event }
  const [deletingEpisodeId, setDeletingEpisodeId] = useState(null);

  // Episode edit modal
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [episodeForm, setEpisodeForm] = useState({ startDate: null, endDate: null, endReason: '', comments: '', sourceSystem: '', sourceContractId: '' });
  const [episodeError, setEpisodeError] = useState('');
  const [isSavingEpisode, setIsSavingEpisode] = useState(false);
  // ADR-168 D5 — the dry run's warnings, awaiting the operator's Confirm.
  const [episodeWarnings, setEpisodeWarnings] = useState(null);
  const editingClosedEpisode = !!editingEpisode && !isActiveEpisode(editingEpisode);

  // Event edit modal
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({ actorType: '', effectiveOn: null, processedAt: null, eventValidationState: '' });
  const [eventError, setEventError] = useState('');
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  const [eventWarnings, setEventWarnings] = useState(null);
  // ADR-168 — the event being edited belongs to a closed contract.
  const [editingEventClosed, setEditingEventClosed] = useState(false);

  // A confirmed preview describes the form as it was; any edit after it
  // must be previewed again.
  useEffect(() => {
    setEpisodeWarnings(null);
  }, [episodeForm]);
  useEffect(() => {
    setEventWarnings(null);
  }, [eventForm]);

  useEffect(() => {
    fetchEmploymentEpisodes();
  }, [employeeId]);

  const fetchEmploymentEpisodes = async () => {
    try {
      setIsLoading(true);
      const data = await employmentApi.getEmploymentEpisodes(employeeId);
      setEpisodes(data);
    } catch (err) {
      showAlert('Failed to load employment episodes', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Episode edit ----
  const openEpisodeModal = (episode) => {
    setEpisodeError('');
    const ref = episodeSourceRef(episode);
    setEpisodeForm({
      startDate: toDayjs(episode.startDate),
      endDate: toDayjs(episode.endDate),
      endReason: episode.endReason || '',
      comments: episode.comments || '',
      // ADR-136: default the source system to 'radar' on an episode that has no
      // stamp yet, so a manually entered contract id can never land without
      // provenance (the field stays editable — an operator backfilling an
      // ingestion id types it over).
      sourceSystem: ref.source ?? (isEpisodeLinked(episode) ? '' : DEFAULT_SOURCE_SYSTEM),
      sourceContractId: ref.contractId != null ? String(ref.contractId) : '',
    });
    setEditingEpisode(episode);
  };

  const closeEpisodeModal = () => {
    setEditingEpisode(null);
    setEpisodeWarnings(null);
  };

  const submitEpisode = async () => {
    setEpisodeError('');

    // Dates are only part of the full ADR-019 surface; the ADR-136 A5 annotation
    // modal doesn't render them, so it has nothing to validate here.
    // ADR-168: a closed contract has stricter rules (end date kept, not after today).
    if (editingClosedEpisode) {
      const closedError = validateClosedEpisodeForm(episodeForm);
      if (closedError) {
        setEpisodeError(closedError);
        return;
      }
    } else if (!isAnnotationOnly) {
      if (!episodeForm.startDate || !episodeForm.startDate.isValid()) {
        setEpisodeError('A valid start date is required.');
        return;
      }
      if (episodeForm.endDate && episodeForm.startDate.isAfter(episodeForm.endDate, 'day')) {
        setEpisodeError('Start date must be on or before end date.');
        return;
      }
    }

    const payload = {
      comments: episodeForm.comments === '' ? null : episodeForm.comments,
    };

    if (!isAnnotationOnly) {
      payload.startDate = toApiDate(episodeForm.startDate);
      payload.endDate = toApiDate(episodeForm.endDate);
    }

    // ADR-168 — a closed contract has an end reason, and it is correctable.
    if (editingClosedEpisode && episodeForm.endReason) {
      payload.endReason = episodeForm.endReason;
    }

    // ADR-049/136: send the source reference when this episode's link is editable —
    // it has none yet, or it is one we own and may be corrected. Re-sending an
    // unchanged value is a harmless idempotent merge server-side. Clearing a field
    // does NOT remove an existing link (the API never clears one); it simply
    // sends nothing for that key.
    //
    // ADR-115 / ADR-160 §5.4 (fixed 2026-09-08): EITHER key may be sent on its
    // own — the API has accepted a source-only reference since ADR-115, but this
    // form only sent the reference when a contract id was typed, so stamping an
    // unstamped episode's Source System by hand (the ADR-160 pre-deploy fix for
    // a person the sync backfill did not reach) saved successfully and wrote
    // nothing. A source alone is a complete, meaningful write.
    if (isSourceRefEditable(editingEpisode)) {
      const source = episodeForm.sourceSystem.trim();
      // ADR-174 §5.5 — a number a person enters is TEXT, always: the API now
      // refuses a JSON number from a person. The old numericAsNumber coercion
      // for ingestion-owned backfills is retired — the Americas sync compares
      // contract ids as String() === String() (radar-ingestion
      // findMatchingEpisode, read 2026-10-12), so a text id matches its numeric
      // source exactly as before, and leading zeros are never lost.
      const contractId = toSourceContractId(episodeForm.sourceContractId);
      // ADR-174 §5.4 rule 3 — emptying the field CLEARS a number radar owns
      // (to move a number to the right contract: clear it here, add it there).
      const existingRef = editingEpisode?.payload?.sourceReference || null;
      const hadNumber = existingRef?.contractId !== undefined && existingRef?.contractId !== null
        && String(existingRef.contractId).trim() !== '';
      const clears = contractId === null && hadNumber && isRadarSourced(existingRef);
      if (contractId !== null || source || clears) {
        payload.sourceReference = {
          ...(contractId !== null ? { contractId } : {}),
          ...(clears ? { contractId: null } : {}),
          ...(source ? { source } : {}),
        };
      }
    }

    try {
      setIsSavingEpisode(true);
      // ADR-136 A5: annotation-only callers go through the narrow endpoint, which
      // enforces the same field allow-list server-side.
      if (isAnnotationOnly) {
        await employmentApi.annotateEmploymentEpisode(editingEpisode.id, payload);
      } else {
        // ADR-168 D5 — a closed contract is previewed first; any warning
        // needs the operator's Confirm before the real save.
        if (editingClosedEpisode && episodeWarnings === null) {
          const preview = await employmentApi.updateEmploymentEpisode(editingEpisode.id, payload, { dryRun: true });
          if (needsConfirmation(preview)) {
            setEpisodeWarnings(preview.warnings);
            return;
          }
        }
        await employmentApi.updateEmploymentEpisode(editingEpisode.id, payload);
      }
      showAlert(editingClosedEpisode ? 'Closed contract corrected' : 'Employment episode updated', 'success');
      closeEpisodeModal();
      await fetchEmploymentEpisodes();
    } catch (err) {
      setEpisodeError(err.message || 'Failed to update employment episode.');
    } finally {
      setIsSavingEpisode(false);
    }
  };

  // ---- Event edit ----
  const openEventModal = (event, episode) => {
    setEventError('');
    setEventForm({
      actorType: event.actorType || '',
      effectiveOn: toDayjs(event.effectiveOn),
      processedAt: toDayjs(event.processedAt),
      eventValidationState: event.eventValidationState || '',
    });
    setEditingEvent(event);
    setEditingEventClosed(!isActiveEpisode(episode));
  };

  const closeEventModal = () => {
    setEditingEvent(null);
    setEventWarnings(null);
  };

  const submitEvent = async () => {
    setEventError('');

    try {
      setIsSavingEvent(true);
      const eventPayload = {
        actorType: eventForm.actorType,
        eventValidationState: eventForm.eventValidationState,
        effectiveOn: toApiDate(eventForm.effectiveOn),
        processedAt: toApiDate(eventForm.processedAt),
      };
      // ADR-168 D5 — an event of a closed contract is previewed first.
      if (editingEventClosed && eventWarnings === null) {
        const preview = await employmentApi.updateEmploymentEvent(editingEvent.id, eventPayload, { dryRun: true });
        if (needsConfirmation(preview)) {
          setEventWarnings(preview.warnings);
          return;
        }
      }
      await employmentApi.updateEmploymentEvent(editingEvent.id, eventPayload);
      showAlert('Employment event updated', 'success');
      closeEventModal();
      await fetchEmploymentEpisodes();
    } catch (err) {
      setEventError(err.message || 'Failed to update employment event.');
    } finally {
      setIsSavingEvent(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <FormContainer>
      <FormSection>
        {episodes.length === 0 ? (
          <EmptyState
            title="No Employment Episodes Found"
            description="This individual doesn't have any employment episodes recorded yet."
          />
        ) : (
          <>
            <ResultCount>
              <span>{episodes.length}</span> {episodes.length === 1 ? 'episode' : 'episodes'} found
            </ResultCount>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {episodes.map((episode, index) => (
                <Accordion
                  key={episode.id}
                  defaultExpanded={index === 0}
                  sx={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: 0,
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<FaChevronDown style={{ color: COLORS.PRIMARY }} />}
                    sx={{
                      backgroundColor: '#ffffff',
                      borderBottom: '1px solid #e0e0e0',
                      '&.Mui-expanded': {
                        minHeight: '48px',
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        '&.Mui-expanded': {
                          margin: '12px 0',
                        }
                      }
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      width: '100%',
                      pr: 2
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>
                        {/* ADR-148 §3.1-3.3 — every row is labelled as a
                            contract, on a three-rung fallback: its number plus
                            type and entity; else type and entity alone (the
                            Enova population, which carries no contract number
                            by ADR-115 §3.1); else the bare noun, matching
                            ContractDetails' own terminal fallback.

                            The `Episode N` ordinal is gone. It was an array
                            position: it renumbered on any ADR-019 correction,
                            named nothing outside radar (a People Partner cannot
                            take it to Enova or to the signed contract), and
                            could not tell two live contracts apart once ADR-144
                            lands (ADR-135 §3.8). The date range beside this
                            label does the distinguishing the ordinal only
                            appeared to do.

                            `employee` is deliberately null: this tab lists
                            HISTORY, and the employee payload's contract fields
                            describe the current contract only (ADR-148 §3.2). */}
                        {contractIdentityLabel(null, episode, { withStartDate: false })
                          || 'Contract'}
                      </Typography>
                      {/* ADR-148 §3.4 (closing ADR-116 §3.5 item 2) — the
                          identity and the date range are two facts, and with
                          only whitespace between them they read as one run of
                          text. A rule and a wider gutter separate them without
                          spending a third separator character: ADR-116 §3.4
                          assigned `·` and `—` one job each and the label above
                          already uses both. */}
                      <Box sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexShrink: 0,
                        pl: 2,
                        borderLeft: '1px solid #E2E8F0',
                        fontSize: '13px',
                        color: '#64748B'
                      }}>
                        <span>
                          {dayjs.utc(episode.startDate).format('DD/MM/YYYY')}
                        </span>
                        <span>-</span>
                        <span>
                          {episode.endDate ? dayjs.utc(episode.endDate).format('DD/MM/YYYY') : 'Present'}
                        </span>
                      </Box>
                      {/* ADR-171 U7 — with more than one open contract the
                          rows say which one anchors the employment */}
                      {openEpisodeCount > 1 && !episode.endDate && (
                        <Chip
                          label={episode.id === primaryEmploymentEpisodeId ? 'Primary' : 'Open'}
                          size="small"
                          variant={episode.id === primaryEmploymentEpisodeId ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 500, fontSize: '12px' }}
                        />
                      )}
                      {episode.employmentEvents && (
                        <Chip
                          label={`${episode.employmentEvents.length} ${episode.employmentEvents.length === 1 ? 'event' : 'events'}`}
                          size="small"
                          sx={{
                            backgroundColor: '#e0e7ff',
                            color: '#4f46e5',
                            fontWeight: 500,
                            fontSize: '12px'
                          }}
                        />
                      )}
                      {(showEpisodeEditor(episode) || canDeleteEpisode(episode)) && (
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                          {showEpisodeEditor(episode) && (
                            <Tooltip title={
                              !isActiveEpisode(episode)
                                ? 'Correct closed contract'
                                : (isAnnotationOnly ? 'Annotate episode' : 'Edit episode')
                            }>
                              <IconButton
                                size="small"
                                onClick={() => openEpisodeModal(episode)}
                                sx={{ color: COLORS.PRIMARY }}
                              >
                                <FaPen size={13} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDeleteEpisode(episode) && (
                            <Tooltip title="Delete contract">
                              <IconButton
                                size="small"
                                aria-label="Delete contract"
                                onClick={() => setDeletingEpisodeId(episode.id)}
                                sx={{ color: '#EF4444' }}
                              >
                                <FaTrash size={13} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    {isEpisodeLinked(episode) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#475569' }}>
                          Source contract:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                          {episodeSourceRef(episode).contractId}
                          {episodeSourceRef(episode).source ? ` (${episodeSourceRef(episode).source})` : ''}
                        </Typography>
                      </Box>
                    )}
                    {episode.comments && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#475569' }}>
                          Comments:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                          {episode.comments}
                        </Typography>
                      </Box>
                    )}

                    {episode.employmentEvents && episode.employmentEvents.length > 0 && (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: '#475569' }}>
                          Employment Events:
                        </Typography>
                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Event Type</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Actor</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Effective Date</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Processed</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Status</TableCell>
                                {showEventActions(episode) && (
                                  <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="right"></TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {episode.employmentEvents.map((event) => (
                                <TableRow
                                  key={event.id}
                                  sx={{
                                    '&:last-child td, &:last-child th': { border: 0 },
                                    '&:hover': { backgroundColor: '#f8fafc' }
                                  }}
                                >
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: COLORS.PRIMARY }}>
                                        {event.eventType.code}
                                      </Typography>
                                      {event.eventType.description && (
                                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                                          {event.eventType.description}
                                        </Typography>
                                      )}
                                      <EventCaption event={event} />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={event.actorType}
                                      size="small"
                                      sx={{
                                        backgroundColor: getActorTypeColor(event.actorType),
                                        color: '#ffffff',
                                        fontWeight: 500,
                                        fontSize: '11px'
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                                      {event.effectiveOn ? dayjs.utc(event.effectiveOn).format('DD/MM/YYYY') : '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                                      {event.processedAt ? dayjs.utc(event.processedAt).format('DD/MM/YYYY HH:mm') : '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={event.eventValidationState}
                                      size="small"
                                      sx={{
                                        backgroundColor: getValidationStateColor(event.eventValidationState),
                                        color: getValidationStateTextColor(event.eventValidationState),
                                        fontWeight: 500,
                                        fontSize: '11px'
                                      }}
                                    />
                                  </TableCell>
                                  {showEventActions(episode) && (
                                    <TableCell align="right">
                                      {canReviseEventOn(event, episode) ? (
                                        <Tooltip title="Edit scheduled amendment">
                                          <IconButton
                                            size="small"
                                            onClick={() => setRevisingAmendment({ episodeId: episode.id, event })}
                                            sx={{ color: COLORS.PRIMARY }}
                                          >
                                            <FaPen size={12} />
                                          </IconButton>
                                        </Tooltip>
                                      ) : (showEventEditors(episode) && isTerminalEvent(event)) ? (
                                        <Tooltip title="Edit event">
                                          <IconButton
                                            size="small"
                                            onClick={() => openEventModal(event, episode)}
                                            sx={{ color: COLORS.PRIMARY }}
                                          >
                                            <FaPen size={12} />
                                          </IconButton>
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title={isTerminalEvent(event) ? 'Editing this event needs the episode modification right' : 'Pending events cannot be edited'}>
                                          <span>
                                            <IconButton size="small" disabled>
                                              <FaPen size={12} />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </>
        )}
      </FormSection>

      {/* ADR-176 — revise a Pending amendment */}
      <EditAmendmentDialog
        open={Boolean(revisingAmendment)}
        onClose={() => setRevisingAmendment(null)}
        episodeId={revisingAmendment?.episodeId}
        amendmentEvent={revisingAmendment?.event || null}
        onRevised={() => { setRevisingAmendment(null); fetchEmploymentEpisodes(); }}
      />

      {/* Contract deletion (ADR-165 A5) */}
      {deletingEpisodeId && (
        <DeleteEpisodeDialog
          episodeId={deletingEpisodeId}
          onClose={() => setDeletingEpisodeId(null)}
          onDeleted={() => {
            setDeletingEpisodeId(null);
            showAlert('Contract deleted', 'success');
            fetchEmploymentEpisodes();
          }}
        />
      )}

      {/* Episode edit modal (ADR-019) */}
      {editingEpisode && (
        <Modal
          isOpen={!!editingEpisode}
          title={editingClosedEpisode
            ? 'Correct Closed Contract'
            : (isAnnotationOnly ? 'Annotate Employment Episode' : 'Edit Employment Episode')}
          onClose={closeEpisodeModal}
          onCancel={closeEpisodeModal}
          onSubmit={submitEpisode}
          submitText={isSavingEpisode ? 'Saving...' : (episodeWarnings ? 'Confirm and save' : 'Save')}
          submitDisabled={isSavingEpisode}
          maxWidth="md"
        >
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {editingClosedEpisode && (
              <Alert severity="info">
                You are correcting a closed contract. The change is recorded in the Employment Change Log.
              </Alert>
            )}
            {episodeError && <Alert severity="error">{episodeError}</Alert>}
            {/* ADR-168 D4/D5 — the dry run's warnings; Save becomes Confirm. */}
            {episodeWarnings && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Check before saving:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {warningLines(episodeWarnings).map((line) => (
                    <Typography component="li" variant="body2" key={line}>{line}</Typography>
                  ))}
                </Box>
              </Alert>
            )}
            {/* ADR-136 A5: the annotation surface deliberately omits the dates —
                on a contract created here they are owned by the employment event
                verbs, so editing them from this modal would bypass the event
                stream. The API rejects them too, not just this UI. */}
            {!isAnnotationOnly && (
            <>
            <DatePicker
              label="Start Date"
              value={episodeForm.startDate}
              onChange={(newValue) => setEpisodeForm((prev) => ({ ...prev, startDate: newValue }))}
              format="DD/MM/YYYY"
              disabled={isSavingEpisode}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  required: true,
                  inputProps: { placeholder: 'DD/MM/YYYY' },
                },
              }}
            />
            <DatePicker
              label="End Date"
              value={episodeForm.endDate}
              onChange={(newValue) => setEpisodeForm((prev) => ({ ...prev, endDate: newValue }))}
              format="DD/MM/YYYY"
              disabled={isSavingEpisode}
              // ADR-168 G2 — a closed contract cannot end after today.
              maxDate={editingClosedEpisode ? dayjs.utc() : undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  required: editingClosedEpisode,
                  // ADR-168 G1 — a closed contract keeps its end date.
                  helperText: editingClosedEpisode
                    ? 'A closed contract keeps an end date. Move it if it is wrong; reopening is a rehire.'
                    : 'Leave empty for "Present"',
                  inputProps: { placeholder: 'DD/MM/YYYY' },
                },
                field: { clearable: !editingClosedEpisode },
              }}
            />
            {editingClosedEpisode && (
              <TextField
                select
                label="End Reason"
                fullWidth
                size="small"
                value={episodeForm.endReason}
                onChange={(e) => setEpisodeForm((prev) => ({ ...prev, endReason: e.target.value }))}
                disabled={isSavingEpisode}
                helperText={episodeForm.endReason ? undefined : 'Not recorded on this contract'}
              >
                {END_REASON_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            )}
            </>
            )}
            <TextField
              label="Comments"
              multiline
              minRows={2}
              fullWidth
              size="small"
              value={episodeForm.comments}
              onChange={(e) => setEpisodeForm({ ...episodeForm, comments: e.target.value })}
            />

            {/* Source reference (ADR-049): link a manually-added episode to its
                source contract. Read-only once linked — the id cannot be re-pointed. */}
            <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5 }}>
              Source reference
            </Typography>
            <TextField
              label="Source System"
              fullWidth
              size="small"
              value={episodeForm.sourceSystem}
              onChange={(e) => setEpisodeForm((prev) => ({ ...prev, sourceSystem: e.target.value }))}
              disabled={isSavingEpisode || !isSourceRefEditable(editingEpisode)}
              helperText={!isSourceRefEditable(editingEpisode)
                ? undefined
                : `Defaults to "${DEFAULT_SOURCE_SYSTEM}" for contracts recorded here; change it when backfilling an ingestion-owned contract (e.g. americas-radar). Naming an ingestion source makes the link read-only from then on.`}
            />
            <TextField
              label="Source Contract ID"
              fullWidth
              size="small"
              value={episodeForm.sourceContractId}
              onChange={(e) => setEpisodeForm((prev) => ({ ...prev, sourceContractId: e.target.value }))}
              disabled={isSavingEpisode || !isSourceRefEditable(editingEpisode)}
              helperText={!isSourceRefEditable(editingEpisode)
                ? 'Owned by an ingestion source; the link cannot be changed here.'
                : isEpisodeLinked(editingEpisode)
                  ? 'Recorded here, so it can be corrected (e.g. a mistyped number).'
                  : 'Link this episode to its contract in the system of record.'}
            />
          </Stack>
        </Modal>
      )}

      {/* Event edit modal (ADR-019) */}
      {editingEvent && (
        <Modal
          isOpen={!!editingEvent}
          title={editingEventClosed ? 'Correct Event on a Closed Contract' : 'Edit Employment Event'}
          onClose={closeEventModal}
          onCancel={closeEventModal}
          onSubmit={submitEvent}
          submitText={isSavingEvent ? 'Saving...' : (eventWarnings ? 'Confirm and save' : 'Save')}
          submitDisabled={isSavingEvent}
          maxWidth="md"
        >
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {editingEventClosed && (
              <Alert severity="info">
                This event belongs to a closed contract. Editing it does not change the contract&apos;s dates.
              </Alert>
            )}
            {eventError && <Alert severity="error">{eventError}</Alert>}
            {eventWarnings && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Check before saving:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {warningLines(eventWarnings).map((line) => (
                    <Typography component="li" variant="body2" key={line}>{line}</Typography>
                  ))}
                </Box>
              </Alert>
            )}
            <TextField
              label="Event Type"
              fullWidth
              size="small"
              value={editingEvent.eventType?.code || ''}
              disabled
              helperText="Event type cannot be changed"
            />
            <TextField
              select
              label="Actor"
              fullWidth
              size="small"
              value={eventForm.actorType}
              onChange={(e) => setEventForm({ ...eventForm, actorType: e.target.value })}
            >
              {Object.values(EVENT_ACTOR_TYPES).map((actor) => (
                <MenuItem key={actor} value={actor}>{actor}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              fullWidth
              size="small"
              value={eventForm.eventValidationState}
              onChange={(e) => setEventForm({ ...eventForm, eventValidationState: e.target.value })}
            >
              {TERMINAL_EVENT_STATES.map((state) => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </TextField>
            <DatePicker
              label="Effective Date"
              value={eventForm.effectiveOn}
              onChange={(newValue) => setEventForm((prev) => ({ ...prev, effectiveOn: newValue }))}
              format="DD/MM/YYYY"
              disabled={isSavingEvent}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  inputProps: { placeholder: 'DD/MM/YYYY' },
                },
                field: { clearable: true },
              }}
            />
            <DatePicker
              label="Processed Date"
              value={eventForm.processedAt}
              onChange={(newValue) => setEventForm((prev) => ({ ...prev, processedAt: newValue }))}
              format="DD/MM/YYYY"
              disabled={isSavingEvent}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  inputProps: { placeholder: 'DD/MM/YYYY' },
                },
                field: { clearable: true },
              }}
            />
          </Stack>
        </Modal>
      )}
    </FormContainer>
  );
};

export default EmploymentEpisodes;
